import React, {
	ButtonHTMLAttributes,
	CSSProperties,
	FocusEventHandler,
	forwardRef,
	ReactNode,
	SyntheticEvent,
	useMemo,
	useState,
} from 'react';
import classNames from 'classnames';
import { BaseButtonGeneralProps, BaseButtonStyleProps, BaseButtonThemeProps } from '../BaseButton';
import { useMounted } from '../../hooks/useMounted';
import rootClasses from './styles/root.module.scss';
import { getAppearanceClasses } from '../BaseButton/utils';
import { isMobileAny } from '../../utils/device';
import { IconButtonSize } from './types';
import { getButtonSizeClass } from './utils';
import { IconButtonContext } from './const';
import { isUndefined } from '../../predicates/type';

export type IconButtonProps = BaseButtonThemeProps &
	Pick<BaseButtonGeneralProps, 'tabIndex' | 'ariaLabel' | 'title' | 'dataTestId'> &
	Pick<BaseButtonStyleProps, 'active' | 'rounded'> & {
		type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
		onFocus?: FocusEventHandler;
		onBlur?: FocusEventHandler;
		onClick?: (e: SyntheticEvent) => void;
		disabled?: boolean;
		size?: IconButtonSize;
		forceSize?: boolean;
		stopPropagation?: boolean;

		className?: string;

		children?: ReactNode;
		dataFunction?: string;
		style?: CSSProperties;
	};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
	(
		{
			disabled,
			active,

			primary,
			link,
			linkDark,
			danger,
			dangerLink,

			size = 'normal',
			rounded,
			type = 'button',

			className,
			style,
			tabIndex,
			ariaLabel,
			title,
			dataTestId,
			forceSize,
			stopPropagation,

			onClick,
			dataFunction,
			children,
		},
		ref,
	) => {
		const { mountedRef } = useMounted();
		const [pressed, setPressed] = useState(false);
		const buttonClassName = classNames(
			rootClasses.root,
			getAppearanceClasses({
				appearance: {
					primary,
					link,
					linkDark,
					danger,
					dangerLink,
				},
				state: {
					disabled,
					active,
					pressed,
					canHover: !isMobileAny,
				},
			}),
			getButtonSizeClass(size),
			className,
			{
				[rootClasses.button_rounded]: rounded,
			},
		);

		const currentTabIndex = disabled ? -1 : tabIndex;

		const handleClick = (e: SyntheticEvent) => {
			if (disabled) {
				return;
			}

			if (stopPropagation) {
				e.stopPropagation();
			}

			if (!isUndefined(onClick)) {
				onClick(e);
			}
		};

		const handleMouseDown = () => {
			if (disabled || active) {
				return;
			}

			setPressed(true);

			setTimeout(() => {
				if (mountedRef.current) {
					setPressed(false);
				}
			}, 100);
		};

		const providerValue = useMemo(() => (forceSize ? null : { size }), [forceSize, size]);

		return (
			<IconButtonContext.Provider value={providerValue}>
				<button
					className={buttonClassName}
					ref={ref}
					aria-label={ariaLabel}
					title={title}
					disabled={disabled}
					data-testid={dataTestId}
					tabIndex={currentTabIndex}
					onMouseDown={handleMouseDown}
					onClick={handleClick}
					type={type}
					data-function={dataFunction}
					style={style}
				>
					<span className={rootClasses.inner}>{children}</span>
				</button>
			</IconButtonContext.Provider>
		);
	},
);

IconButton.displayName = 'IconButton';
